import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import AdminItemRow from './adminItemRow';
import Helmet from 'react-helmet';
import { useItems } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import utils from '../../utils/utils';
import CustomPaginator from '../../components/paginator';

const RESULTS_PER_PAGE = 30;

const AdminItems = () => {
  const [sortBy, setSortBy] = useState('idDesc');
  const [activePage, setActivePage] = useState(1);

  const [typedQuery, setTypedQuery] = useState(null); // for detecting input change
  const [query, setQuery] = useState(null); // for triggering actual search
  const queryTimer = useRef(null);

  const {
    isLoading: loading,
    result,
    isError,
    error,
    refetch,
  } = useItems({
    sortBy,
    query,
    offset: (activePage - 1) * RESULTS_PER_PAGE,
    admin: true,
  });

  useEffect(() => {
    clearTimeout(queryTimer.current);
    queryTimer.current = setTimeout(() => {
      setQuery(typedQuery);
    }, 500);
  }, [typedQuery]);

  useEffect(() => {
    refetch();
  }, [sortBy, activePage, query]);

  function setSortMethod(newSortBy) {
    let sortParam;

    switch (newSortBy) {
      case 'price':
        sortParam = sortBy === 'priceAsc' ? 'priceDesc' : 'priceAsc';
        break;
      case 'id':
      default:
        sortParam = sortBy === 'idAsc' ? 'idDesc' : 'idAsc';
    }

    setSortBy(sortParam);
  }

  const { formatMessage } = useIntl();

  const paginator = (
    <div className="m-b-3 m-t-3">
      <CustomPaginator
        activePage={activePage}
        resultsPerPage={RESULTS_PER_PAGE}
        setActivePage={(newNumber) => {
          if (newNumber !== activePage) {
            setActivePage(newNumber);
          }
        }}
        totalCount={result?.totalCount || 0}
      />
    </div>
  );

  return (
    <div>
      <Helmet>
        <title>{formatMessage({ id: 'admin.items.title' })}</title>
      </Helmet>
      <h3>
        <FormattedMessage id={'admin.items.title'} />
      </h3>

      <div className="m-b-3">
        <Link className="btn btn-default m-r-2" to="/admin/items/create">
          <FormattedMessage id={'admin.button.create'} />
        </Link>
        <Link className="btn btn-default m-r-2" to="/admin/items/order">
          <FormattedMessage id={'admin.items.button.ordering'} />
        </Link>
      </div>

      <Loader show={loading} />
      {isError ? <ErrorMessage error={utils.getErrorMessageFromResponse(error)} /> : ''}

      {paginator}

      <div className="text-right">
        <div className="form-group field field-string d-inline-block">
          <input
            className="form-control"
            placeholder="Search"
            type="text"
            value={typedQuery}
            onChange={(e) => setTypedQuery(e.target.value)}
          />
        </div>
      </div>

      <table className="table table-striped admin__table admin__table m-bottom-margin">
        <thead>
          <tr>
            <th onClick={() => setSortMethod('id')} className="sortable" scope="col">
              <FormattedMessage id={'admin.items.table.name'} />
            </th>
            <th onClick={() => setSortMethod('price')} className="sortable" scope="col">
              <FormattedMessage id={'admin.items.table.price'} />
            </th>
            <th scope="col">
              <FormattedMessage id={'admin.items.table.marketplace'} />
            </th>
            <th scope="col">
              <FormattedMessage id={'admin.items.table.actions'} />
            </th>
          </tr>
        </thead>
        <tbody>
          {result?.items.map((item, index) => (
            <AdminItemRow item={item} key={index} />
          ))}
        </tbody>
      </table>
      {paginator}
    </div>
  );
};

AdminItems.propTypes = {
  setLoading: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
};

AdminItems.defaultProps = {};

export default AdminItems;
